import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import type {
    IPendingWorkupFormValues,
    IPendingWorkupModel,
    IPendingWorkupSubmitResult,
} from '@/types/surveys/pending-workup';

const PENDING_WORKUP_ENDPOINT = '/api/register/pending-workup';

const defaultPendingWorkupModel: IPendingWorkupModel = {
    id: 0,
    editKey: '',
    hasBeenScheduled: false,
    scheduledDateTime: null,
    hasCompleted: null,
    treatment: null,
    convenience: null,
    facility: null,
    commendations: '',
    comments: '',
    languageData: {},
};

export const usePendingWorkup = (registrationId: number, editKey: string) => {
    const url = `${PENDING_WORKUP_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel<IPendingWorkupModel>(url, defaultPendingWorkupModel);

    const { submit, isSubmitting, submitError } = useApiSubmit<IPendingWorkupFormValues, DashRegistrationStep, IPendingWorkupSubmitResult>(
        url,
        (values) => {
            // The sub-questions only exist while their parent answer is Yes, so anything
            // behind a No is sent empty rather than as whatever was typed before.
            const hasBeenScheduled = toBoolOrNull(values.hasBeenScheduled);
            const hasCompleted = hasBeenScheduled === true ? toBoolOrNull(values.hasCompleted) : null;

            return {
                hasBeenScheduled,
                scheduledDateTime: hasBeenScheduled === true && values.scheduledDateTime !== '' ? values.scheduledDateTime : null,
                hasCompleted,
                treatment: hasCompleted === true ? toIntOrNull(values.treatment) : null,
                convenience: hasCompleted === true ? toIntOrNull(values.convenience) : null,
                facility: hasCompleted === true ? toIntOrNull(values.facility) : null,
                commendations: hasCompleted === true ? values.commendations : '',
                comments: hasCompleted === true ? values.comments : '',
            };
        },
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
